import React, { useState, useEffect, useMemo } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import RoleGuard, { usePermission } from '../roles/RoleGuard';
import Modal from '../../components/ui/Modal';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import {
  RotateCcw, Clock, CheckCircle2, XCircle, ShieldCheck, RefreshCw,
  Ban, Check, X, ChevronLeft, ChevronRight, Calendar, Package,
  AlertTriangle, Loader2, FileText, MessageCircle, Gift, CreditCard,
  CornerUpLeft, Settings, Plus, Trash2, Save, User, Mail, Phone, Hash,
  History,
} from 'lucide-react';
import './ReturnRequests.css';

const STATUS_CONFIG = {
  PENDING:            { label: 'Pending review', icon: Clock,        pill: 'rt-pill-warn'    },
  MERCHANT_APPROVED:  { label: 'Approved',       icon: CheckCircle2, pill: 'rt-pill-info'    },
  MERCHANT_REJECTED:  { label: 'Rejected',       icon: XCircle,      pill: 'rt-pill-danger'  },
  RESOLVED:           { label: 'Resolved',       icon: CheckCircle2, pill: 'rt-pill-success' },
  CANCELLED:          { label: 'Cancelled',      icon: Ban,          pill: 'rt-pill-muted'   },
};

const REASON_LABELS = {
  DEFECTIVE: 'Defective',
  WRONG_ITEM: 'Wrong item',
  NOT_AS_DESCRIBED: 'Not as described',
  CHANGED_MIND: 'Changed mind',
  SIZE_FIT_ISSUE: 'Size/fit issue',
  QUALITY_ISSUE: 'Quality issue',
  ARRIVED_LATE: 'Arrived late',
  OTHER: 'Other',
};

const RESOLUTION_OPTIONS = [
  { value: 'REFUNDED',     label: 'Refunded',     desc: 'Customer received a full or partial refund', icon: CreditCard },
  { value: 'REPLACED',     label: 'Replaced',     desc: 'Customer received a replacement item',       icon: CornerUpLeft },
  { value: 'STORE_CREDIT', label: 'Store credit', desc: 'Customer received store credit',             icon: Gift },
  { value: 'NO_ACTION',    label: 'No action',    desc: 'Issue resolved without action needed',       icon: XCircle },
];

const ITEMS_PER_PAGE = 25;

const ReturnRequests = () => {
  const { isAuthenticated, loading: authLoading } = useSecureAuth();
  const toast = useToast();
  const canEdit = usePermission('orders', 'edit');

  const [activeTab, setActiveTab] = useState('requests');

  const [returnRequests, setReturnRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, requestId: null });
  const [rejectNotes, setRejectNotes] = useState('');
  const [resolveModal, setResolveModal] = useState({ open: false, requestId: null });
  const [resolution, setResolution] = useState('');
  const [resolveNotes, setResolveNotes] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [policySettings, setPolicySettings] = useState({
    enabled: true,
    returnWindowDays: 14,
    policyText: '',
    conditions: ['Item must be unused', 'Original packaging required', 'Receipt/proof of purchase required'],
  });
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [newCondition, setNewCondition] = useState('');

  const loadReturnRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/merchant/return-requests');
      if (response.data.success) {
        setReturnRequests(response.data.returnRequests || []);
      } else {
        setError('Failed to load return requests');
      }
    } catch (err) {
      console.error('Load return requests error:', err);
      setError(err.response?.data?.error || 'Failed to load return requests');
    } finally {
      setLoading(false);
    }
  };

  const loadPolicySettings = async () => {
    try {
      setLoadingPolicy(true);
      const response = await axios.get('/merchant/return-requests/policy');
      if (response.data.success) {
        setPolicySettings({
          enabled: response.data.policy.enabled,
          returnWindowDays: response.data.policy.returnWindowDays,
          policyText: response.data.policy.policyText || '',
          conditions: response.data.policy.conditions || [],
        });
      }
    } catch (err) {
      console.error('Load policy settings error:', err);
      toast.error('Failed to load return policy settings');
    } finally {
      setLoadingPolicy(false);
    }
  };

  const savePolicySettings = async () => {
    try {
      setSavingPolicy(true);
      const response = await axios.put('/merchant/return-requests/policy', {
        enabled: policySettings.enabled,
        returnWindowDays: policySettings.returnWindowDays,
        policyText: policySettings.policyText,
        conditions: policySettings.conditions,
      });
      if (response.data.success) {
        toast.success('Return policy saved');
      } else {
        toast.error(response.data.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save policy settings error:', err);
      toast.error(err.response?.data?.error || 'Failed to save return policy settings');
    } finally {
      setSavingPolicy(false);
    }
  };

  const addCondition = () => {
    if (newCondition.trim() && !policySettings.conditions.includes(newCondition.trim())) {
      setPolicySettings((p) => ({ ...p, conditions: [...p.conditions, newCondition.trim()] }));
      setNewCondition('');
    }
  };

  const removeCondition = (index) => {
    setPolicySettings((p) => ({ ...p, conditions: p.conditions.filter((_, i) => i !== index) }));
  };

  const handleApprove = async (requestId) => {
    try {
      setActionLoading(requestId);
      setError('');
      const response = await axios.post(`/merchant/return-requests/${requestId}/approve`, { merchantNotes: '' });
      if (response.data.success) {
        toast.success('Return approved');
        loadReturnRequests();
        if (selectedRequest?.id === requestId) setSelectedRequest(null);
      } else {
        setError(response.data.error || 'Failed to approve return request');
      }
    } catch (err) {
      console.error('Approve return error:', err);
      setError(err.response?.data?.error || 'Failed to approve return request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectNotes.trim()) {
      toast.error('Please give a reason for rejection');
      return;
    }
    try {
      setActionLoading(rejectModal.requestId);
      setError('');
      const response = await axios.post(`/merchant/return-requests/${rejectModal.requestId}/reject`, {
        merchantNotes: rejectNotes,
      });
      if (response.data.success) {
        toast.success('Return rejected');
        loadReturnRequests();
        if (selectedRequest?.id === rejectModal.requestId) setSelectedRequest(null);
        closeRejectModal();
      } else {
        setError(response.data.error || 'Failed to reject return request');
      }
    } catch (err) {
      console.error('Reject return error:', err);
      setError(err.response?.data?.error || 'Failed to reject return request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async () => {
    if (!resolution) {
      toast.error('Pick a resolution type');
      return;
    }
    try {
      setActionLoading(resolveModal.requestId);
      setError('');
      const response = await axios.post(`/merchant/return-requests/${resolveModal.requestId}/resolve`, {
        resolution,
        merchantNotes: resolveNotes,
      });
      if (response.data.success) {
        toast.success('Return resolved');
        loadReturnRequests();
        if (selectedRequest?.id === resolveModal.requestId) setSelectedRequest(null);
        closeResolveModal();
      } else {
        setError(response.data.error || 'Failed to resolve return request');
      }
    } catch (err) {
      console.error('Resolve return error:', err);
      setError(err.response?.data?.error || 'Failed to resolve return request');
    } finally {
      setActionLoading(null);
    }
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, requestId: null });
    setRejectNotes('');
  };

  const closeResolveModal = () => {
    setResolveModal({ open: false, requestId: null });
    setResolution('');
    setResolveNotes('');
  };

  useEffect(() => {
    if (isAuthenticated) loadReturnRequests();
  }, [isAuthenticated]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy]);

  useEffect(() => {
    if (activeTab === 'policy') loadPolicySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredRequests = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return returnRequests
      .filter((r) => {
        if (statusFilter !== 'all' && r.status !== statusFilter) return false;
        if (!q) return true;
        return (
          r.requestNumber?.toLowerCase().includes(q) ||
          r.order?.orderNumber?.toLowerCase().includes(q) ||
          r.customer?.firstName?.toLowerCase().includes(q) ||
          r.customer?.lastName?.toLowerCase().includes(q) ||
          r.reason?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) =>
        sortBy === 'oldest'
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt)
      );
  }, [returnRequests, searchTerm, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const stats = useMemo(() => ({
    pending:  returnRequests.filter((r) => r.status === 'PENDING').length,
    approved: returnRequests.filter((r) => r.status === 'MERCHANT_APPROVED').length,
    resolved: returnRequests.filter((r) => r.status === 'RESOLVED').length,
  }), [returnRequests]);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatDateTime = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const formatReason = (reason) => {
    if (!reason) return 'Not specified';
    return REASON_LABELS[reason] || reason.charAt(0).toUpperCase() + reason.slice(1).replace(/_/g, ' ');
  };

  const getResolutionLabel = (res) =>
    RESOLUTION_OPTIONS.find((r) => r.value === res)?.label || res;

  const statusOf = (status) =>
    STATUS_CONFIG[status] || { label: status, icon: Clock, pill: 'rt-pill-muted' };

  if (!isAuthenticated && !authLoading) {
    return (
      <Shell>
        <div className="rt-page">
          <div className="rt-empty">
            <div className="rt-empty-icon"><ShieldCheck size={22} /></div>
            <h2 className="rt-empty-title">Sign in required</h2>
            <p className="rt-empty-desc">Please log in to view return requests.</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <RoleGuard
        permission="orders:view"
        fallback={
          <div className="rt-page">
            <div className="rt-empty">
              <div className="rt-empty-icon"><ShieldCheck size={22} /></div>
              <h2 className="rt-empty-title">No access</h2>
              <p className="rt-empty-desc">You don't have permission to view return requests.</p>
            </div>
          </div>
        }
      >
        <div className="rt-page">

          {/* ── Header ─────────────────────────────────────────────── */}
          <header className="rt-header">
            <div className="rt-header-text">
              <h1 className="rt-title">Return requests</h1>
              <p className="rt-subtitle">Approve, reject, or resolve customer return requests.</p>
            </div>
            {activeTab === 'requests' && (
              <div className="rt-header-actions">
                <button
                  type="button"
                  className="rt-btn rt-btn-ghost"
                  onClick={loadReturnRequests}
                  disabled={loading}
                  aria-label="Refresh"
                  title="Refresh"
                >
                  <RefreshCw size={16} className={loading ? 'rt-spin' : ''} />
                  <span className="rt-btn-text">Refresh</span>
                </button>
              </div>
            )}
          </header>

          {/* ── Tabs (pill style per CLAUDE.md) ────────────────────── */}
          <div className="rt-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'requests'}
              className={`rt-tab${activeTab === 'requests' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              Requests
              {stats.pending > 0 && (
                <span className="rt-tab-badge">{stats.pending}</span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'policy'}
              className={`rt-tab${activeTab === 'policy' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('policy')}
            >
              Return policy
            </button>
          </div>

          {/* ── Error banner ───────────────────────────────────────── */}
          {error && (
            <div className="rt-banner rt-banner-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
              <button type="button" className="rt-banner-close" onClick={() => setError('')} aria-label="Dismiss">×</button>
            </div>
          )}

          {/* ── Requests tab ───────────────────────────────────────── */}
          {activeTab === 'requests' && (
            <>
              <section className="rt-stat-grid">
                <div className="rt-stat">
                  <span className="rt-stat-label">Pending</span>
                  <span className="rt-stat-value">{stats.pending}</span>
                </div>
                <div className="rt-stat">
                  <span className="rt-stat-label">Approved</span>
                  <span className="rt-stat-value rt-stat-value-info">{stats.approved}</span>
                </div>
                <div className="rt-stat">
                  <span className="rt-stat-label">Resolved</span>
                  <span className="rt-stat-value rt-stat-value-success">{stats.resolved}</span>
                </div>
              </section>

              <section className="rt-filters">
                <div className="rt-search">
                  <input
                    type="text"
                    className="rt-input rt-search-input"
                    placeholder="Search by request, order, customer, or reason…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="rt-search-clear"
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <select
                  className="rt-input rt-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All status</option>
                  <option value="PENDING">Pending review</option>
                  <option value="MERCHANT_APPROVED">Approved</option>
                  <option value="MERCHANT_REJECTED">Rejected</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <select
                  className="rt-input rt-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </section>

              {loading ? (
                <div className="rt-loading">
                  <Loader2 size={20} className="rt-spin" />
                  <span>Loading return requests…</span>
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="rt-empty">
                  <div className="rt-empty-icon"><RotateCcw size={22} /></div>
                  <h2 className="rt-empty-title">
                    {returnRequests.length === 0 ? 'No return requests yet' : 'No matches'}
                  </h2>
                  <p className="rt-empty-desc">
                    {returnRequests.length === 0
                      ? "Customers can request returns from their order page — they'll show up here."
                      : 'No requests match your current filters.'}
                  </p>
                  {(searchTerm || statusFilter !== 'all') && (
                    <button
                      type="button"
                      className="rt-btn rt-btn-secondary"
                      onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <p className="rt-results">
                    Showing {paginatedRequests.length} of {filteredRequests.length} request{filteredRequests.length === 1 ? '' : 's'}
                  </p>
                  <ul className="rt-list">
                    {paginatedRequests.map((request) => {
                      const status = statusOf(request.status);
                      const StatusIcon = status.icon;
                      return (
                        <li key={request.id} className="rt-row">
                          <button
                            type="button"
                            className="rt-row-main"
                            onClick={() => setSelectedRequest(request)}
                            aria-label={`View return ${request.requestNumber || ''}`}
                          >
                            <span className="rt-row-id">{request.requestNumber || 'N/A'}</span>
                            <span className="rt-row-customer">
                              {request.customer?.firstName} {request.customer?.lastName}
                            </span>
                            <span className="rt-row-reason">{formatReason(request.reason)}</span>
                            <span className={`rt-pill ${status.pill}`}>
                              <StatusIcon size={10} />
                              {status.label}
                            </span>
                            <span className="rt-row-date">{formatDate(request.createdAt)}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {totalPages > 1 && (
                    <nav className="rt-pagination" aria-label="Pagination">
                      <button
                        type="button"
                        className="rt-page-btn"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <span className="rt-page-info">
                        Page <strong>{currentPage}</strong> of {totalPages}
                      </span>
                      <button
                        type="button"
                        className="rt-page-btn"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </nav>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Policy tab ─────────────────────────────────────────── */}
          {activeTab === 'policy' && (
            loadingPolicy ? (
              <div className="rt-loading">
                <Loader2 size={20} className="rt-spin" />
                <span>Loading policy settings…</span>
              </div>
            ) : (
              <section className="rt-policy">

                <article className="rt-card">
                  <div className="rt-card-body">
                    <div className="rt-toggle-row">
                      <div className="rt-toggle-text">
                        <span className="rt-toggle-label">Accept returns</span>
                        <span className="rt-toggle-sub">When off, customers can't request returns from their order page.</span>
                      </div>
                      <label className="rt-toggle">
                        <input
                          type="checkbox"
                          checked={!!policySettings.enabled}
                          onChange={() => setPolicySettings((p) => ({ ...p, enabled: !p.enabled }))}
                        />
                        <span className="rt-toggle-track" />
                      </label>
                    </div>
                  </div>
                </article>

                <article className="rt-card">
                  <div className="rt-card-body">
                    <div className="rt-card-head">
                      <h2 className="rt-card-title">Return window</h2>
                      <p className="rt-card-sub">Days a customer has to request a return after delivery.</p>
                    </div>
                    <div className="rt-days">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        className="rt-input rt-days-input"
                        value={policySettings.returnWindowDays}
                        onChange={(e) => setPolicySettings((p) => ({
                          ...p,
                          returnWindowDays: parseInt(e.target.value, 10) || 14,
                        }))}
                      />
                      <span className="rt-days-suffix">days</span>
                    </div>
                  </div>
                </article>

                <article className="rt-card">
                  <div className="rt-card-body">
                    <div className="rt-card-head">
                      <h2 className="rt-card-title">Conditions</h2>
                      <p className="rt-card-sub">Bullets the customer must agree to when requesting a return.</p>
                    </div>
                    <ul className="rt-conditions">
                      {policySettings.conditions.length === 0 && (
                        <li className="rt-conditions-empty">No conditions yet.</li>
                      )}
                      {policySettings.conditions.map((condition, index) => (
                        <li key={index} className="rt-condition">
                          <span>{condition}</span>
                          <button
                            type="button"
                            className="rt-condition-remove"
                            onClick={() => removeCondition(index)}
                            aria-label={`Remove ${condition}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="rt-condition-add">
                      <input
                        type="text"
                        className="rt-input"
                        value={newCondition}
                        onChange={(e) => setNewCondition(e.target.value)}
                        placeholder="Add a condition…"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCondition(); } }}
                      />
                      <button
                        type="button"
                        className="rt-btn rt-btn-secondary"
                        onClick={addCondition}
                        disabled={!newCondition.trim()}
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>
                  </div>
                </article>

                <article className="rt-card">
                  <div className="rt-card-body">
                    <div className="rt-card-head">
                      <h2 className="rt-card-title">Policy text</h2>
                      <p className="rt-card-sub">Shown to customers on the return-request page.</p>
                    </div>
                    <textarea
                      className="rt-input rt-textarea"
                      value={policySettings.policyText}
                      onChange={(e) => setPolicySettings((p) => ({ ...p, policyText: e.target.value }))}
                      placeholder="e.g. Items can be returned within 14 days of delivery…"
                      rows={6}
                    />
                  </div>
                </article>

                <div className="rt-policy-actions">
                  <button
                    type="button"
                    className="rt-btn rt-btn-primary"
                    onClick={savePolicySettings}
                    disabled={savingPolicy}
                  >
                    {savingPolicy ? <Loader2 size={14} className="rt-spin" /> : <Save size={14} />}
                    Save policy
                  </button>
                </div>
              </section>
            )
          )}

          {/* ── Detail modal ───────────────────────────────────────── */}
          <Modal
            isOpen={!!selectedRequest}
            onClose={() => setSelectedRequest(null)}
            title={selectedRequest ? `Return ${selectedRequest.requestNumber || ''}` : ''}
            padded={false}
            footer={
              selectedRequest && canEdit && (selectedRequest.status === 'PENDING' || selectedRequest.status === 'MERCHANT_APPROVED') ? (
                selectedRequest.status === 'PENDING' ? (
                  <>
                    <button
                      type="button"
                      className="rt-btn rt-btn-danger"
                      disabled={actionLoading === selectedRequest.id}
                      onClick={() => setRejectModal({ open: true, requestId: selectedRequest.id })}
                    >
                      <Ban size={14} /> Reject
                    </button>
                    <button
                      type="button"
                      className="rt-btn rt-btn-primary"
                      disabled={actionLoading === selectedRequest.id}
                      onClick={() => handleApprove(selectedRequest.id)}
                    >
                      {actionLoading === selectedRequest.id
                        ? <Loader2 size={14} className="rt-spin" />
                        : <Check size={14} />}
                      Approve
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="rt-btn rt-btn-primary"
                    disabled={actionLoading === selectedRequest.id}
                    onClick={() => setResolveModal({ open: true, requestId: selectedRequest.id })}
                  >
                    <Gift size={14} /> Mark resolved
                  </button>
                )
              ) : null
            }
          >
            {selectedRequest && (() => {
              const status = statusOf(selectedRequest.status);
              const StatusIcon = status.icon;
              return (
                <div className="rt-detail">
                  <div className="rt-detail-status-row">
                    <span className={`rt-pill ${status.pill}`}>
                      <StatusIcon size={11} />
                      {status.label}
                    </span>
                    <span className="rt-detail-id">
                      <Hash size={12} /> {selectedRequest.requestNumber || '—'}
                    </span>
                  </div>

                  <dl className="rt-detail-kv">
                    <div className="rt-detail-kv-row">
                      <dt><Package size={13} /> Order</dt>
                      <dd>{selectedRequest.order?.orderNumber || '—'}</dd>
                    </div>
                    <div className="rt-detail-kv-row">
                      <dt><User size={13} /> Customer</dt>
                      <dd>{selectedRequest.customer?.firstName} {selectedRequest.customer?.lastName}</dd>
                    </div>
                    <div className="rt-detail-kv-row">
                      <dt><Mail size={13} /> Email</dt>
                      <dd>{selectedRequest.customer?.email || '—'}</dd>
                    </div>
                    {selectedRequest.customer?.phone && (
                      <div className="rt-detail-kv-row">
                        <dt><Phone size={13} /> Phone</dt>
                        <dd>{selectedRequest.customer.phone}</dd>
                      </div>
                    )}
                    <div className="rt-detail-kv-row">
                      <dt><Calendar size={13} /> Requested</dt>
                      <dd>{formatDateTime(selectedRequest.createdAt)}</dd>
                    </div>
                    <div className="rt-detail-kv-row">
                      <dt><FileText size={13} /> Reason</dt>
                      <dd>{formatReason(selectedRequest.reason)}</dd>
                    </div>
                  </dl>

                  {selectedRequest.customerNotes && (
                    <div className="rt-detail-section">
                      <h3 className="rt-detail-section-title">
                        <MessageCircle size={13} /> Customer notes
                      </h3>
                      <p className="rt-detail-notes">{selectedRequest.customerNotes}</p>
                    </div>
                  )}

                  {selectedRequest.items && selectedRequest.items.length > 0 && (
                    <div className="rt-detail-section">
                      <h3 className="rt-detail-section-title">
                        <Package size={13} /> Items
                      </h3>
                      <ul className="rt-detail-items">
                        {selectedRequest.items.map((item, idx) => (
                          <li key={idx} className="rt-detail-item">
                            <span className="rt-detail-item-name">{item.productName || item.name}</span>
                            <span className="rt-detail-item-qty">× {item.quantity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedRequest.resolution && (
                    <div className="rt-detail-section">
                      <h3 className="rt-detail-section-title">
                        <Gift size={13} /> Resolution
                      </h3>
                      <div className="rt-detail-resolution">
                        <span className="rt-pill rt-pill-success">
                          <Check size={11} /> {getResolutionLabel(selectedRequest.resolution)}
                        </span>
                        {selectedRequest.resolvedAt && (
                          <span className="rt-detail-resolution-date">
                            {formatDateTime(selectedRequest.resolvedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedRequest.merchantNotes && (
                    <div className="rt-detail-section">
                      <h3 className="rt-detail-section-title">
                        <History size={13} /> Your notes
                      </h3>
                      <p className="rt-detail-notes">{selectedRequest.merchantNotes}</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </Modal>

          {/* ── Reject modal ───────────────────────────────────────── */}
          <Modal
            isOpen={rejectModal.open}
            onClose={closeRejectModal}
            title="Reject return request"
            padded={false}
            footer={
              <>
                <button type="button" className="rt-btn rt-btn-secondary" onClick={closeRejectModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="rt-btn rt-btn-danger"
                  disabled={!rejectNotes.trim() || !!actionLoading}
                  onClick={handleReject}
                >
                  {actionLoading ? <Loader2 size={14} className="rt-spin" /> : <Ban size={14} />}
                  Reject
                </button>
              </>
            }
          >
            <div className="rt-form">
              <p className="rt-form-help">
                The customer sees this reason in their order page and rejection email.
              </p>
              <div className="rt-field">
                <label className="rt-field-label">Reason</label>
                <textarea
                  className="rt-input rt-textarea"
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Why is this return being rejected?"
                  rows={4}
                />
              </div>
            </div>
          </Modal>

          {/* ── Resolve modal ──────────────────────────────────────── */}
          <Modal
            isOpen={resolveModal.open}
            onClose={closeResolveModal}
            title="Mark return as resolved"
            padded={false}
            footer={
              <>
                <button type="button" className="rt-btn rt-btn-secondary" onClick={closeResolveModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="rt-btn rt-btn-primary"
                  disabled={!resolution || !!actionLoading}
                  onClick={handleResolve}
                >
                  {actionLoading ? <Loader2 size={14} className="rt-spin" /> : <Check size={14} />}
                  Mark resolved
                </button>
              </>
            }
          >
            <div className="rt-form">
              <p className="rt-form-help">
                Record what happened with this return so the customer record stays accurate.
              </p>
              <div className="rt-field">
                <label className="rt-field-label">Resolution</label>
                <div className="rt-radio-list" role="radiogroup">
                  {RESOLUTION_OPTIONS.map(({ value, label, desc, icon: Icon }) => {
                    const active = resolution === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`rt-radio-row${active ? ' is-active' : ''}`}
                        onClick={() => setResolution(value)}
                      >
                        <span className="rt-radio-dot" />
                        <span className="rt-radio-icon"><Icon size={16} /></span>
                        <span className="rt-radio-body">
                          <span className="rt-radio-label">{label}</span>
                          <span className="rt-radio-desc">{desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="rt-field">
                <label className="rt-field-label">Notes (optional)</label>
                <textarea
                  className="rt-input rt-textarea"
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Anything else worth recording about this resolution…"
                  rows={3}
                />
              </div>
            </div>
          </Modal>

        </div>
      </RoleGuard>
    </Shell>
  );
};

export default ReturnRequests;
